import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { soccerPath } from '../utils/paths';
import { mediaUrl } from '../utils/blogUtils';
import '../components/PlayerDetailProfile.css';
import './PublicFormationPlayerProfile.css';

const formatPosition = (p) =>
  p ? p.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()) : '—';
const formatSkillCategory = (c) =>
  c ? c.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (x) => x.toUpperCase()) : '—';
const formatDate = (d) =>
  d ? new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—';
const formatTeamSide = (side) => (side === 'RED' ? 'Red' : side === 'BLUE' ? 'Blue' : side || '—');

const formatGameStatLine = (game) => {
  const parts = [];
  if (game.goals > 0) parts.push(`${game.goals}G`);
  if (game.assists > 0) parts.push(`${game.assists}A`);
  if (game.saves > 0) parts.push(`${game.saves}SV`);
  if (game.defended > 0) parts.push(`${game.defended}DEF`);
  return parts.length > 0 ? parts.join(', ') : 'Played';
};

const HIGHLIGHT_META = {
  GOAL: { label: 'Goal', icon: '⚽', className: 'goal' },
  ASSIST: { label: 'Assist', icon: '🎯', className: 'assist' },
  MOTM: { label: 'Man of the Match', icon: '🏆', className: 'motm' },
  HAT_TRICK: { label: 'Hat-trick', icon: '🔥', className: 'hat-trick' },
  CLEAN_SHEET: { label: 'Clean Sheet', icon: '🧤', className: 'clean-sheet' },
  CUSTOM: { label: 'Award', icon: '⭐', className: 'custom' },
};

const formatEventType = (eventType, manual) => {
  if (manual || eventType === 'MANUAL') return 'Pickup / Manual';
  if (eventType === 'MATCH') return 'Match';
  return 'Practice';
};

const StatCard = ({ value, label }) => (
  <div className="player-detail-profile__stat-card">
    <span className="player-detail-profile__stat-value">{value}</span>
    <span className="player-detail-profile__stat-label">{label}</span>
  </div>
);

const PublicFormationPlayerProfile = () => {
  const { token, playerId } = useParams();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadProfile = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await api.getPublicFormationPlayerProfile(token, playerId, user?.id);
      setProfile(data);
    } catch (err) {
      setProfile(null);
      setError(err.message || 'Failed to load player record');
    } finally {
      setLoading(false);
    }
  }, [token, playerId, user?.id]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  if (loading) {
    return <div className="loading">Loading player record…</div>;
  }

  if (error) {
    return (
      <div className="player-detail-profile">
        <Link to={soccerPath(`/public/practice-formation/${token}`)} className="btn btn-secondary">
          ← Back to lineup
        </Link>
        <div className="alert alert-error" style={{ marginTop: '1rem' }}>{error}</div>
      </div>
    );
  }

  if (!profile?.player) {
    return (
      <div className="player-detail-profile">
        <div className="empty-state">Player record not found.</div>
      </div>
    );
  }

  const { player, clubName, practiceTitle, ownProfile, playingRecord } = profile;
  const stats = playingRecord?.stats || {};
  const highlights = playingRecord?.highlights || [];
  const gameLog = playingRecord?.gameLog || [];
  const photoUrl = player.profilePictureUrl ? mediaUrl(player.profilePictureUrl) : null;
  const hasStats =
    (stats.totalGoals || 0) > 0 ||
    (stats.totalAssists || 0) > 0 ||
    (stats.manOfMatchCount || 0) > 0 ||
    (stats.practicesPlayed || 0) > 0;

  return (
    <div className="player-detail-profile public-formation-player-profile">
      <div className="player-detail-profile__nav">
        <Link to={soccerPath(`/public/practice-formation/${token}`)} className="btn btn-secondary">
          ← Back to lineup
        </Link>
        {ownProfile && user?.id && (
          <Link to={soccerPath(`/clubs/${profile.clubId}/players/${player.id}`)} className="btn btn-primary">
            View full club profile
          </Link>
        )}
      </div>

      <div className="player-detail-profile__card">
        <div className="player-detail-profile__header">
          <div className="player-detail-profile__avatar-wrap">
            {photoUrl ? (
              <img src={photoUrl} alt={player.name} className="player-detail-profile__avatar" />
            ) : (
              <div className="player-detail-profile__avatar-placeholder">
                {player.name?.charAt(0)?.toUpperCase() || '?'}
              </div>
            )}
          </div>
          <div className="player-detail-profile__identity">
            <p className="player-detail-profile__club">{clubName}</p>
            <h1>{player.name}</h1>
            {ownProfile && <span className="badge badge-info">Your record</span>}
            <p className="player-detail-profile__position">
              {formatPosition(player.position)}
              {player.skillCategory && (
                <span> · {formatSkillCategory(player.skillCategory)}</span>
              )}
            </p>
            {practiceTitle && (
              <p className="public-formation-player-profile__lineup-context">
                Lineup: {practiceTitle}
              </p>
            )}
          </div>
        </div>

        <div className="player-detail-profile__stats-section">
          <h2>Playing Record</h2>
          <div className="player-detail-profile__stats-grid">
            <StatCard value={stats.totalGoals || 0} label="Goals" />
            <StatCard value={stats.totalAssists || 0} label="Assists" />
            <StatCard value={stats.manOfMatchCount || 0} label="Man of the Match" />
            <StatCard value={stats.practicesPlayed || 0} label="Practices" />
          </div>
          {(stats.totalSaves > 0 || stats.totalDefended > 0) && (
            <div className="player-detail-profile__stats-extra">
              {stats.totalSaves > 0 && <span>{stats.totalSaves} saves</span>}
              {stats.totalDefended > 0 && <span>{stats.totalDefended} defended</span>}
            </div>
          )}
          {!hasStats && (
            <p className="player-detail-profile__stats-empty">
              No recorded stats yet. Goals and awards appear after practices and matches.
            </p>
          )}
        </div>

        {gameLog.length > 0 && (
          <div className="public-formation-player-profile__game-log">
            <h2>Practice game log</h2>
            <ul className="public-formation-player-profile__game-log-list">
              {gameLog.map((game) => (
                <li key={`${game.practiceId}-${game.teamSide}`} className="public-formation-player-profile__game-log-item">
                  <div>
                    <strong>{game.practiceTitle}</strong>
                    <span className="public-formation-player-profile__game-log-meta">
                      {formatDate(game.practiceDate)} · {formatTeamSide(game.teamSide)} team
                      {game.scoreRed != null && game.scoreBlue != null
                        ? ` · Red ${game.scoreRed}–${game.scoreBlue} Blue`
                        : ''}
                    </span>
                    <p>{formatGameStatLine(game)}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {highlights.length > 0 && (
          <div className="player-detail-profile__highlights">
            <h2>Highlights</h2>
            <ul className="player-detail-profile__timeline">
              {highlights.map((item, index) => {
                const meta = HIGHLIGHT_META[item.type] || HIGHLIGHT_META.GOAL;
                return (
                  <li
                    key={`${item.type}-${item.eventId}-${index}`}
                    className={`player-detail-profile__timeline-item player-detail-profile__timeline-item--${meta.className}`}
                  >
                    <div className="player-detail-profile__timeline-marker">{meta.icon}</div>
                    <div className="player-detail-profile__timeline-content">
                      <div className="player-detail-profile__timeline-header">
                        <span className="player-detail-profile__timeline-type">{meta.label}</span>
                        <span className="player-detail-profile__timeline-date">
                          {formatDate(item.eventDate)}
                        </span>
                      </div>
                      <p className="player-detail-profile__timeline-title">{item.eventTitle}</p>
                      {item.description && (
                        <p className="player-detail-profile__timeline-desc">{item.description}</p>
                      )}
                      <span className="player-detail-profile__timeline-badge">
                        {formatEventType(item.eventType, item.manual)}
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicFormationPlayerProfile;
